<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      isResizingConversationList: false,
      conversationListResizeStartX: 0,
      conversationListResizeStartWidth: 0,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
    conversationListWidth() {
      return this.uiSettings.conversation_list_width || 380;
    },
    shouldShowConversationListResizeHandle() {
      return (
        this.showConversationList &&
        this.showMessageView &&
        !this.isOnExpandedLayout
      );
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    document.addEventListener('mousemove', this.onConversationListResizeMove);
    document.addEventListener('mouseup', this.onConversationListResizeEnd);
    document.addEventListener('touchmove', this.onConversationListResizeMove, {
      passive: false,
    });
    document.addEventListener('touchend', this.onConversationListResizeEnd);
  },

  beforeUnmount() {
    document.removeEventListener('mousemove', this.onConversationListResizeMove);
    document.removeEventListener('mouseup', this.onConversationListResizeEnd);
    document.removeEventListener('touchmove', this.onConversationListResizeMove);
    document.removeEventListener('touchend', this.onConversationListResizeEnd);
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    getResizeClientX(event) {
      return event.touches ? event.touches[0].clientX : event.clientX;
    },
    clampConversationListWidth(width) {
      return Math.min(Math.max(width, 300), 620);
    },
    onConversationListResizeStart(event) {
      this.isResizingConversationList = true;
      this.conversationListResizeStartX = this.getResizeClientX(event);
      this.conversationListResizeStartWidth = this.conversationListWidth;
      Object.assign(document.body.style, {
        cursor: 'col-resize',
        userSelect: 'none',
      });
      event.preventDefault();
    },
    onConversationListResizeMove(event) {
      if (!this.isResizingConversationList) return;

      const nextWidth = this.clampConversationListWidth(
        this.conversationListResizeStartWidth +
          this.getResizeClientX(event) -
          this.conversationListResizeStartX
      );
      this.updateUISettings({ conversation_list_width: nextWidth });
      event.preventDefault();
    },
    onConversationListResizeEnd() {
      if (!this.isResizingConversationList) return;

      this.isResizingConversationList = false;
      Object.assign(document.body.style, { cursor: '', userSelect: '' });
      this.updateUISettings({
        conversation_list_width: this.clampConversationListWidth(
          this.conversationListWidth
        ),
      });
    },
    resetConversationListWidth() {
      this.updateUISettings({ conversation_list_width: 380 });
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-list-width="conversationListWidth"
      @conversation-load="onConversationLoad"
    />
    <div
      v-if="shouldShowConversationListResizeHandle"
      class="hidden md:block relative h-full w-1 cursor-col-resize z-40 group flex-shrink-0"
      @mousedown="onConversationListResizeStart"
      @touchstart="onConversationListResizeStart"
      @dblclick="resetConversationListWidth"
    >
      <div
        class="absolute top-0 h-full w-px left-1/2 -translate-x-1/2 bg-n-weak group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizingConversationList }"
      />
    </div>
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>
